import { createContext, use } from 'react';
import type { IdToken } from '@/auth/token';

/**
 * Two states, deliberately — there is no `initializing`.
 *
 * The `sessionStorage` mirror is read *synchronously* during the provider's first render,
 * so a reload into `/play/{id}` with a live token is `signed_in` on the very first frame
 * and never flashes the sign-in screen. Without a token the answer is `signed_out`
 * immediately; One Tap may still resolve it a moment later, and when it does the guard
 * carries the player back to where they were. A third "we don't know yet" state would buy
 * only a spinner that sometimes never resolves.
 */
export type AuthStatus = 'signed_in' | 'signed_out';

/**
 * Why the session ended. `expired` is the interesting one: it is a 401 mid-session or a
 * token that outlived its `exp`, and it is the case D8 exists for — the route is preserved
 * and the player is re-prompted, rather than being dumped at a blank sign-in page.
 */
export type SignedOutReason = 'expired' | 'signed_out' | null;

/** The GIS script's own lifecycle, which is not the player's sign-in state. */
export type IdentityStatus = 'loading' | 'ready' | 'unavailable';

export interface AuthContextValue {
  status: AuthStatus;
  token: IdToken | null;
  reason: SignedOutReason;
  identityStatus: IdentityStatus;
  /** Why identity is unavailable, when it is — a missing client id, a blocked script. */
  identityError: string | null;
  /** Prompt One Tap. Safe to call when already signed in; it does nothing. */
  signIn: () => void;
  /** The player's own sign-out: clears the mirror and disables auto-select. */
  signOut: () => void;
  /** Renders Google's own button into `element`. Null is a no-op (the element unmounted). */
  renderButton: (element: HTMLElement | null) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = use(AuthContext);
  if (value === null) {
    throw new Error('useAuth() used outside <AuthProvider>.');
  }
  return value;
}
